import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import Modal from './Modal';
import axios from 'axios';
import Table from '../Listing/Table';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {faArrowUp, faArrowDown, faXmark, faArrowsUpDown, faTag, faCalendarDays, faBoxesStacked} from '@fortawesome/free-solid-svg-icons';
import { formatRelativeDate, formatExpirationDate } from '../../utils/FormatDate';
import { API_BASE_URL } from '../../api';

const isExpiredBatch = (batch) => {
	if (!batch?.expirationDate) return false;
	const today = new Date();
	today.setHours(0, 0, 0, 0);
	return new Date(batch.expirationDate) < today;
};

const StatAvatar = ({ icon, bg, color }) => (
	<div style={{
		width: 34, height: 34, borderRadius: 9, flexShrink: 0,
		background: bg, color,
		display: 'flex', alignItems: 'center', justifyContent: 'center',
		fontSize: 14,
	}}>
		<FontAwesomeIcon icon={icon} />
	</div>
);

const StockBatchMovementModal = ({batchId, productName, batch, show, onClose}) => {
	const [batchMovements, setBatchMovements] = useState([]);
	const [page, setPage] = useState(0);
	const [totalPages, setTotalPages] = useState(0);
	const [totalElements, setTotalElements] = useState(0);

	const columns = [
	    {
	      Header: "#",
	      accessor: "id",
	      Cell: (row) => <span className="text-muted">{row.id}</span>
	    },
	    {
	      Header: "Quantité",
	      accessor: "quantity",
		  className: 'text-center',
			maxWidth: '100px',
	      sortable: true,
	      Cell: (row) => 
	      	<span className={`fw-bold ${row.movementType === 'INBOUND' ? 'text-success' : 'text-danger'}`}>
	      		<FontAwesomeIcon className="me-2" icon={row.movementType == 'INBOUND' ? faArrowUp : faArrowDown} />
	      		<span className="pe-1">{row.movementType == 'INBOUND' ? "+" : "-"}</span>
	      		{row.quantity}
	      	</span>
	    },
	    {
	      Header: "Description",
	      accessor: "description",
	      Cell: (row) => {
	        if (row.description) return <span>{row.description}</span>;
	        const defaults = {
	          STOCK_IN:   'Entrée en stock',
	          INVENTORY:  'Ajustement d\'inventaire',
	          RETURN:     'Retour vente',
	          EXCHANGE:   'Échange',
	          ORDER:      'Vente',
	          MANUAL_OUT: 'Sortie manuelle',
	        };
	        return <span className="text-muted fst-italic">{defaults[row.sourceType] ?? '—'}</span>;
	      }
	    },
	    {
	      Header: "Date",
	      accessor: "movementDate",
		  maxWidth: '150px',
	      sortable: true,
			className: "text-center",
	      Cell: (row) => <span className="text-muted">{formatRelativeDate(row.movementDate)}</span>
	    }
	];

	// checked rows — the export takes only these, or the whole batch when none is checked
	const selectedIdsRef = useRef([]);
	useEffect(() => { if (show) selectedIdsRef.current = []; }, [show, batchId]);

	const handleExport = async (format) => {
		if (format === 'email') { toast.info('Export par email bientôt disponible'); return; }
		const params = new URLSearchParams({ format });
		if (selectedIdsRef.current.length > 0) params.set('ids', selectedIdsRef.current.join(','));
		try {
			const response = await fetch(`${API_BASE_URL}/api/v1/stock/batch/${batchId}/movements/export?${params}`, { credentials: 'include' });
			if (!response.ok) throw new Error(`HTTP ${response.status}`);
			const filename = (response.headers.get('Content-Disposition') || '').match(/filename=(.+)/)?.[1] || `mouvements-lot.${format}`;
			const url = URL.createObjectURL(await response.blob());
			const a = document.createElement('a');
			a.href = url;
			a.download = filename;
			document.body.appendChild(a);
			a.click();
			document.body.removeChild(a);
			URL.revokeObjectURL(url);
		} catch (err) {
			console.error('Export error:', err);
			toast.error("Échec de l'export");
		}
	};

	const fetchBatchMovements = () => {
	    axios.get(`${API_BASE_URL}/api/v1/stock/batch/${batchId}/movements`, {
	    	withCredentials: true,
			params: { 
	          page,
	        }
	    })
	      .then(response => {
	        setBatchMovements(response.data.content);
	        setTotalPages(response.data.totalPages);
	        setTotalElements(response.data.totalElements);
	      })
	      .catch(error => {
	        console.error("Failed to fetch product details", error);
	      });
	  };

	useEffect(() => {
	    if (show && batchId) {
	      fetchBatchMovements();
	    }
	}, [page, show, batchId]);

	const title = productName
		? `Mouvements du lot · ${productName}${batch?.expirationDate ? ` (péremption ${formatExpirationDate(batch.expirationDate)})` : ''}`
		: 'Mouvements du lot';

	const expired = isExpiredBatch(batch);

	return (
		<Modal
        id="stockBatchMovementModal"
        classSize="modal-lg"
        title={title}
        show={show}
        onClose={onClose}
        icon={faArrowsUpDown}
        iconBg="#eff6ff"
        iconColor="#2563eb"
        noBodyPadding
        footer={
        	<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
        		<span style={{ fontSize: 13, color: '#6b7280' }}>
        			<strong style={{ color: '#111827' }}>{totalElements}</strong> mouvement{totalElements > 1 ? 's' : ''}
        		</span>
        		<button type="button" className="btn btn-light btn-sm" onClick={onClose}>
        			Fermer
        		</button>
        	</div>
        }
      >
      	{batch && (
      		// the body has no padding (table flush with the modal) — only this row keeps a gutter
      		<div style={{ display: 'flex', alignItems: 'stretch', gap: 10, padding: '14px 24px', borderBottom: '1px solid #eef0f3' }}>
      			<div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0' }}>
      				<StatAvatar icon={faTag} bg="#eff6ff" color="#2563eb" />
      				<div>
      					<p style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', margin: '0 0 4px' }}>
      						N° de lot
      					</p>
      					<p style={{ fontSize: 14, fontWeight: 700, color: '#111827', margin: 0 }}>
      						{batch.batchNumber || '—'}
      					</p>
      				</div>
      			</div>
      			<div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0' }}>
      				<StatAvatar icon={faCalendarDays} bg={expired ? '#fef2f2' : '#f0fdf4'} color={expired ? '#dc2626' : '#059669'} />
      				<div>
      					<p style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', margin: '0 0 4px' }}>
      						Expiration
      					</p>
      					<p style={{ fontSize: 14, fontWeight: 700, color: expired ? '#dc2626' : '#111827', margin: 0 }}>
      						{batch.expirationDate ? formatExpirationDate(batch.expirationDate) : '—'}
      					</p>
      				</div>
      			</div>
      			<div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0' }}>
      				<StatAvatar
      					icon={faBoxesStacked}
      					bg={batch.quantity > 0 ? '#ecfdf5' : batch.quantity < 0 ? '#fef2f2' : '#f3f4f6'}
      					color={batch.quantity > 0 ? '#059669' : batch.quantity < 0 ? '#dc2626' : '#6b7280'}
      				/>
      				<div>
      					<p style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', margin: '0 0 4px' }}>
      						Quantité actuelle
      					</p>
      					<p style={{
      						display: 'flex', alignItems: 'center', gap: 6,
      						fontSize: 14, fontWeight: 700, margin: 0,
      						color: batch.quantity > 0 ? '#059669' : batch.quantity < 0 ? '#dc2626' : '#6b7280',
      					}}>
      						{batch.quantity !== 0 && (
      							<FontAwesomeIcon icon={batch.quantity > 0 ? faArrowUp : faArrowDown} style={{ fontSize: 12 }} />
      						)}
      						{batch.quantity}
      					</p>
      				</div>
      			</div>
      		</div>
      	)}
      	<Table
          columns={columns}
          data={batchMovements}
          page={page}
          totalPages={totalPages}
          changePage={(newPage) => setPage(newPage)}
          totalElements={totalElements}
          rowId="id"
          multiSelect
          onSelectionChange={(ids) => { selectedIdsRef.current = ids; }}
          onExport={handleExport}
        />
      </Modal>
	);
};

export default StockBatchMovementModal;